import React, { useState, useRef, useEffect } from "react";
import { HelpCircle, X, Send, Sparkles, ChevronDown } from "lucide-react";

/* ─── Knowledge base ───────────────────── */
const KB = [
  {
    patterns: ["vendor crm", "vendor directory", "sync", "my vendors", "import vendor"],
    answer: "**Vendor CRM** is your vendor database that builds itself. Hit **Sync from Events** to pull in every vendor who has applied to any of your events — it imports their info, tracks which events they attended, and calculates their total revenue with you. From there you can favorite the ones you want back, block ones who caused issues, add tags, and bulk-export your favorites when planning a new event."
  },
  {
    patterns: ["notif", "send update", "email vendor", "text vendor", "message vendor", "sms"],
    answer: 'Go to **Notifications** in the sidebar. Type what you need to say in plain English in the AI Assist bar (e.g. "parking lot B is closed") and AI drafts a professional message instantly. Then pick who to send to — all vendors, favorites, or vendors from a specific event — toggle Email / SMS / In-app, and hit Send.'
  },
  {
    patterns: ["ticket", "sell ticket", "check in", "attendee", "ticket type"],
    answer: "**Ticketing** is under the PRO section in the sidebar. After you publish an event, you'll be prompted to set up tickets. Create ticket types (General, VIP, etc.), set prices and quantities, and your public ticket page is live immediately. On event day, go to Check In from the Ticketing dashboard to scan or search attendees."
  },
  {
    patterns: ["analytic", "revenue", "stat", "report", "performance"],
    answer: "**Analytics** (PRO) shows total revenue, application counts, approval rates, and year-over-year trends across all your events. It updates automatically as applications come in and payments are processed."
  },
  {
    patterns: ["event", "create event", "publish", "draft", "new event"],
    answer: "Click **+ Create Event** (the floating button on the dashboard or events page). Fill in the details, upload photos, set your vendor categories and fees. When you're ready, hit Publish — Pebble generates a public event page instantly. After publishing, you'll be prompted to set up tickets if you want to sell attendee tickets."
  },
  {
    patterns: ["marketplace", "application", "approve", "vendor appl"],
    answer: "**Marketplace Vendors** shows all vendor applications for your events. You can review applications, approve or decline vendors, and communicate with them. Approved vendors appear in your event roster and are automatically synced to your Vendor CRM."
  },
  {
    patterns: ["pro", "upgrade", "subscription", "plan", "premium"],
    answer: "**PRO** unlocks Vendor CRM, Analytics, and Ticketing. To upgrade, click the **Upgrade** button next to the PRO section in the sidebar, or go to Account → Plan."
  },
  {
    patterns: ["spanish", "language", "translate", "es", "idioma"],
    answer: "Click the **ES** button in the top-right corner to switch the entire platform to Spanish. Your preference is saved, so the platform stays in Spanish on your next visit."
  },
  {
    patterns: ["setting", "profile", "account", "password", "email"],
    answer: "Go to **Settings** in the sidebar. From there you can update your profile, business information, notification preferences, and account settings."
  },
  {
    patterns: ["support", "help", "contact", "problem", "issue", "bug"],
    answer: "You can reach the Pebble support team at [support@pebbletheapp.com](mailto:support@pebbletheapp.com) or through the Support page in the sidebar. I'm here to answer quick questions about how to use specific features!"
  },
];

const SUGGESTIONS = [
  "How do I sync my vendors?",
  "How do I send a message to vendors?",
  "How does ticketing work?",
  "What's included in PRO?",
];

const findAnswer = (input) => {
  const q = input.toLowerCase();
  for (const entry of KB) {
    if (entry.patterns.some(p => q.includes(p))) return entry.answer;
  }
  return "I'm not sure about that one — try asking about Vendor CRM, Notifications, Ticketing, Analytics, Events, or PRO features. You can also reach support via the sidebar.";
};

/* Minimal markdown renderer */
const renderMd = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : p
  );
};

/* ─── Component ──────────────────────────────────────── */
const HelpBot = () => {
  const [open, setOpen]       = useState(false);
  const [input, setInput]     = useState("");
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hey! I'm your Pebble assistant. Ask me anything about how to use the platform." }
  ]);
  const [thinking, setThinking] = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, thinking]);

  const sendMessage = async (text) => {
    const q = (text || input).trim();
    if (!q) return;
    setInput("");
    setMessages(prev => [...prev, { from: "user", text: q }]);
    setThinking(true);
    await new Promise(r => setTimeout(r, 600 + Math.random() * 400));
    const answer = findAnswer(q);
    setThinking(false);
    setMessages(prev => [...prev, { from: "bot", text: answer }]);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-20 right-6 z-50 flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-full shadow-lg hover:bg-gray-800 transition text-sm font-medium"
      >
        {open ? <X className="w-4 h-4" /> : <HelpCircle className="w-4 h-4" />}
        {open ? "Close" : "Help"}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-32 right-6 z-50 w-80 sm:w-96 bg-white border border-gray-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{ maxHeight: "520px" }}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-gray-900 rounded-full flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Pebble Assistant</p>
                <p className="text-xs text-gray-400">Ask me how to use anything</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  m.from === "user"
                    ? "bg-gray-900 text-white rounded-br-sm"
                    : "bg-gray-100 text-gray-800 rounded-bl-sm"
                }`}>
                  {m.from === "bot" ? renderMd(m.text) : m.text}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="flex justify-start">
                <div className="bg-gray-100 px-3 py-2 rounded-2xl rounded-bl-sm flex gap-1">
                  {[0,1,2].map(i => (
                    <span key={i} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: `${i * 150}ms` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggestions (only when few messages) */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => sendMessage(s)}
                  className="text-xs px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full text-gray-600 hover:bg-gray-100 transition">
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-3 pb-3 pt-1 border-t border-gray-100 flex gap-2">
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
              placeholder="Ask anything…"
              className="flex-1 text-sm px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || thinking}
              className={`p-2 rounded-xl transition ${
                input.trim() && !thinking
                  ? "bg-gray-900 text-white hover:bg-gray-800"
                  : "bg-gray-100 text-gray-300"
              }`}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpBot;
